import React from 'react';
import { TopicArea, Category, Tag, Content } from '../../types';
import SearchBar from '../UI/SearchBar';
import CategoryFilter from '../UI/CategoryFilter';
import TimelineItem from './TimelineItem';
import './MobileTimelineView.css';

interface Props {
  contents: Content[];
  searchQ: string;
  onSearch: (q: string) => void;
  categoryId: number | null;
  onCategorySelect: (id: number | null) => void;
  selectedTopicArea: number | null;
  onTopicAreaChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  topicAreas: TopicArea[];
  allCategories: Category[];
  selectedAreaInfo: {
    topicArea: TopicArea | null;
    categories: Category[];
    tags: Tag[];
  };
  showTopicSummary: boolean;
  onToggleTopicSummary: () => void;
  onContentClick: (contentId: number) => void;
}

const MobileTimelineView: React.FC<Props> = ({
  contents,
  searchQ,
  onSearch,
  categoryId,
  onCategorySelect,
  selectedTopicArea,
  onTopicAreaChange,
  topicAreas,
  allCategories,
  selectedAreaInfo,
  showTopicSummary,
  onToggleTopicSummary,
  onContentClick,
}) => {
  // 날짜 순 정렬
  const sortedContents = React.useMemo(() => {
    return [...contents].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [contents]);

  // 연도 그룹화 및 렌더링에 활용
  let lastYear = -1;

  return (
    <div className="mobile-timeline-container">
      {/* 모바일 조작 컨트롤 바 */}
      <div className="mobile-control-bar">
        <div className="mobile-control-row">
          <SearchBar onSearch={onSearch} />
        </div>
        
        <div className="mobile-control-row flex-row">
          <div className="mobile-select-wrapper">
            <select
              value={selectedTopicArea || ''}
              onChange={onTopicAreaChange}
              className="mobile-select"
            >
              <option value="">주제영역 전체</option>
              {topicAreas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.title}
                </option>
              ))}
            </select>
          </div>

          {selectedAreaInfo.topicArea && (
            <button 
              className={`mobile-summary-toggle-btn ${showTopicSummary ? 'active' : ''}`}
              onClick={onToggleTopicSummary}
            >
              요약 {showTopicSummary ? '▲' : '▼'}
            </button>
          )}
        </div>

        <div className="mobile-control-row">
          <CategoryFilter onSelect={onCategorySelect} />
        </div>

        {/* 모바일 주제 요약 오버레이 카드 */}
        {selectedAreaInfo.topicArea && showTopicSummary && (
          <div className="mobile-topic-summary-card">
            <h4>{selectedAreaInfo.topicArea.title}</h4>
            <div className="mobile-topic-meta-badges">
              {selectedAreaInfo.tags.slice(0, 3).map(tag => (
                <span key={tag.id} className="mobile-tag-badge">
                  🏷️ {tag.name}
                </span>
              ))}
            </div>
            <p className="mobile-topic-summary-text">{selectedAreaInfo.topicArea.summary}</p>
            <div className="mobile-topic-date">
              📅 기간: {selectedAreaInfo.topicArea.start_year}년 ~ {selectedAreaInfo.topicArea.is_current === 'Y' ? '현재' : `${selectedAreaInfo.topicArea.end_year}년`}
            </div>
          </div>
        )}
      </div>

      {/* 세로축 타임라인 본체 */}
      <div className="mobile-timeline-body">
        {sortedContents.length === 0 ? (
          <div className="mobile-timeline-empty">
            {selectedTopicArea 
              ? '해당 주제영역에 등록된 사건 기록이 없습니다.' 
              : '주제영역을 선택하거나 검색어를 입력하여 사건을 탐색해보세요.'}
          </div>
        ) : (
          <div className="mobile-timeline-stream">
            {/* 세로 중앙 기점 타임라인 축선 */}
            <div className="mobile-timeline-axis-line" />

            {sortedContents.map((content) => {
              const currentDate = new Date(content.timestamp);
              const currentYear = currentDate.getFullYear();
              
              // 연도가 변경되었을 때 연도 뱃지 출력 플래그 설정
              let showYearHeader = false;
              if (currentYear !== lastYear) {
                showYearHeader = true;
                lastYear = currentYear;
              }

              return (
                <React.Fragment key={content.id}>
                  {showYearHeader && (
                    <div className="mobile-timeline-year-header">
                      <span className="mobile-timeline-year-text">{currentYear}년</span>
                    </div>
                  )}
                  
                  <div 
                    className="mobile-timeline-item-row"
                    onClick={() => onContentClick(content.id)}
                  >
                    {/* 축선 위의 타임라인 노드 마커 */}
                    <div className="mobile-timeline-node-marker" />
                    
                    {/* 카드 영역 */}
                    <div className="mobile-timeline-card-container">
                      <TimelineItem content={content} searchQuery={searchQ} categories={allCategories} />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileTimelineView;
